/**
 * A train's itinerary as its detail draws it (TrainItinerary.tsx): the legs in travel order, and between
 * two legs the change, where and how long the wait. The backend sends the legs only for a journey with
 * changes; a leg missing a station or a time takes the journey's own at its ends, as the old kiosk did.
 */
import type { Train } from '../session';
import type { Journey } from './trip';

export interface ItineraryLeg {
  trainType: string;
  trainNumber: string;
  departure: string;
  origin: string;
  arrival: string;
  destination: string;
}

export interface ItineraryChange {
  station: string;
  /** Minutes between arriving and leaving again; null when a time is missing. */
  waitMin: number | null;
}

export interface Itinerary {
  legs: ItineraryLeg[];
  /** `changes[i]` is between `legs[i]` and `legs[i + 1]`. */
  changes: ItineraryChange[];
}

const minutesOf = (hhmm: string): number | null => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

/** The wait between an arrival and the next departure, across midnight too. */
export function waitMinutes(arrival: string, departure: string): number | null {
  const a = minutesOf(arrival);
  const d = minutesOf(departure);
  if (a === null || d === null) return null;
  return (d - a + 24 * 60) % (24 * 60);
}

/**
 * How many times a journey changes: its legs say it when it has them (two legs, one change), else its count.
 * One answer for every place that shows it (the card's line, «Diretto», the button), so a provider whose count
 * disagrees with its legs never hides the changes the legs show.
 */
export function changesOf(train: Pick<Train, 'legs' | 'changes'>): number {
  return train.legs.length > 1 ? train.legs.length - 1 : train.changes;
}

export function itineraryOf(train: Pick<Train, 'legs' | 'departure_station' | 'arrival_station' | 'departure_time' | 'arrival_time'>): Itinerary {
  const last = train.legs.length - 1;
  const legs = train.legs.map((l, i) => ({
    trainType: l.train_type,
    trainNumber: l.train_id,
    departure: l.departure_time || (i === 0 ? train.departure_time : ''),
    origin: l.departure_station || (i === 0 ? train.departure_station : ''),
    arrival: l.arrival_time || (i === last ? train.arrival_time : ''),
    destination: l.arrival_station || (i === last ? train.arrival_station : ''),
  }));
  return itineraryFrom(legs);
}

/** The itinerary of legs already in order and filled (a trip's journey, widgets/trip.ts): the changes between them. */
export function itineraryFrom(legs: ItineraryLeg[]): Itinerary {
  const changes = legs.slice(0, -1).map((leg, i) => ({
    station: leg.destination || legs[i + 1]!.origin,
    waitMin: waitMinutes(leg.arrival, legs[i + 1]!.departure),
  }));
  return { legs, changes };
}

/**
 * What a card needs to show an item's itinerary (TrainItinerary.tsx), whatever drew the item: its id as the
 * backend names it (the detail's `item`), its position as shown (show_detail's `idx`), how many changes, the
 * itinerary.
 */
export interface ItineraryOf {
  item: string;
  idx: number;
  changes: number;
  itinerary: Itinerary;
  /** The train chosen or bought (the summary, the ticket): its touch says «il mio treno», not its position. */
  chosen?: boolean;
}

/** A trip's journey (the summary, the ticket): the chosen train, at its position; none without one. */
export function journeyItinerary(journey: Journey): ItineraryOf | null {
  if (journey.idx === null) return null;
  return { item: journey.trainNumber, idx: journey.idx, changes: journey.changes, itinerary: itineraryFrom(journey.legs), chosen: true };
}

/** A train on the results, at its position. */
export function trainItinerary(train: Train, idx: number): ItineraryOf {
  return { item: train.train_id, idx, changes: changesOf(train), itinerary: itineraryOf(train) };
}
